import { useEffect, useMemo } from 'react';
import { RefreshCw } from 'lucide-react';
import type {
  OperationsDashboardSnapshot,
  OperationsEnvironmentSnapshot,
  OperationsHealth,
} from '@tcvs/shared';
import { useLocation, useNavigate } from 'react-router-dom';
import { DashboardChart } from '../dashboard/DashboardChart';
import {
  buildOperationsHelpContext,
  openOperationsHelp,
  publishOperationsHelpContext,
} from '../help/operations-help-context';
import { operationsCostChartOption } from './operations-charts';
import { useOperationsApi } from './operations-api';
import { useDashboardRefresh, type DashboardCache } from '../dashboard/use-dashboard-refresh';

const REFRESH_INTERVAL_MS = 300_000;
const WALLBOARD_REFRESH_INTERVAL_MS = 300_000;

function number(value: number): string {
  return value.toLocaleString();
}

function usd(value: number | undefined): string {
  if (value === undefined) return 'Unavailable';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function usdUnitCost(value: number | undefined): string {
  if (value === undefined) return 'Unavailable';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: value < 0.01 ? 4 : 2,
    maximumFractionDigits: value < 0.01 ? 6 : 2,
  }).format(value);
}

function age(value: string | undefined): string {
  if (value === undefined) return 'Pending';
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return 'Unavailable';
  const minutes = Math.max(0, Math.round((Date.now() - timestamp) / 60_000));
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  return `${Math.round(minutes / 60)}h ago`;
}

export function OperationsPage({
  embeddedWallboard = false,
  refreshCache,
}: {
  embeddedWallboard?: boolean;
  refreshCache?: DashboardCache<OperationsDashboardSnapshot>;
} = {}): JSX.Element {
  const api = useOperationsApi();
  const location = useLocation();
  const navigate = useNavigate();
  const wallboard =
    embeddedWallboard || new URLSearchParams(location.search).get('display') === 'wallboard';
  const { result, refreshing, refresh } = useDashboardRefresh(
    api.summary,
    wallboard ? WALLBOARD_REFRESH_INTERVAL_MS : REFRESH_INTERVAL_MS,
    refreshCache,
  );
  const snapshot = result?.ok ? result.data : null;
  const error = result && !result.ok ? (result.message ?? result.error) : null;

  useEffect(() => {
    if (snapshot !== null) {
      publishOperationsHelpContext(buildOperationsHelpContext(snapshot));
    }
  }, [snapshot]);

  useEffect(() => () => publishOperationsHelpContext(undefined), []);

  const environments = snapshot?.environments ?? [];
  const costOption = useMemo(
    () => operationsCostChartOption(environments, wallboard),
    [environments, wallboard],
  );

  const toggleWallboard = (): void => {
    const search = new URLSearchParams(location.search);
    if (wallboard) search.delete('display');
    else search.set('display', 'wallboard');
    void navigate({ pathname: '/admin/operations', search: search.toString() }, { replace: true });
  };

  return (
    <div className="tcvs-operations" data-wallboard={wallboard ? 'true' : 'false'}>
      <header className="tcvs-dashboard-heading tcvs-operations-heading">
        <div>
          <h2>System operations</h2>
          <p>
            {snapshot === null
              ? 'Loading service health'
              : `Updated ${age(snapshot.generatedAt)} · costs cached ${snapshot.costCacheHours} hours`}
            {refreshing ? ' · Refreshing' : ''}
          </p>
        </div>
        <div className="tcvs-dashboard-actions">
          {wallboard ? null : (
            <button type="button" className="tcvs-btn-secondary" onClick={openOperationsHelp}>
              Ask TCVS Help
            </button>
          )}
          <button type="button" onClick={() => void refresh(true)} disabled={refreshing}>
            Refresh
          </button>
          {embeddedWallboard ? null : (
            <button
              type="button"
              className="tcvs-icon-button"
              onClick={toggleWallboard}
              title={wallboard ? 'Exit wallboard view' : 'Open wallboard view'}
              aria-label={wallboard ? 'Exit wallboard view' : 'Open wallboard view'}
            >
              {wallboard ? '×' : '⛶'}
            </button>
          )}
        </div>
      </header>

      {error !== null ? <p className="tcvs-error">Refresh failed: {error}</p> : null}
      {snapshot === null && error === null ? <p>Loading operational health…</p> : null}

      <div className="tcvs-operations-environments">
        {environments.map((environment) => (
          <EnvironmentOverview
            key={`${environment.accountId}:${environment.stage}`}
            environment={environment}
            refreshing={refreshing}
            requestDiagnostics={() => void refresh(true, () => api.summary(true))}
          />
        ))}
      </div>

      {environments.length > 0 ? (
        <div className="tcvs-operations-detail-grid">
          <section className="tcvs-operations-band">
            <header className="tcvs-dashboard-chart-header">
              <h3>AWS cost this month</h3>
              <span>Net unblended cost; refreshed at most every 12 hours</span>
            </header>
            <DashboardChart option={costOption} label="Daily AWS cost by environment" />
          </section>
          <section className="tcvs-operations-band">
            <header className="tcvs-dashboard-chart-header">
              <h3>Needs attention</h3>
              <span>Active alarms and non-empty queues</span>
            </header>
            <AttentionList environments={environments} wallboard={wallboard} />
          </section>
        </div>
      ) : null}

      <section className="tcvs-operations-band tcvs-operations-processes">
        <header className="tcvs-dashboard-chart-header">
          <h3>Highest activity processes</h3>
          <span>Lambda activity during the past 24 hours</span>
        </header>
        <ProcessTable environments={environments} />
      </section>
    </div>
  );
}

function EnvironmentOverview({
  environment,
  refreshing,
  requestDiagnostics,
}: {
  environment: OperationsEnvironmentSnapshot;
  refreshing: boolean;
  requestDiagnostics: () => void;
}): JSX.Element {
  const current = hasCurrentMetrics(environment);
  const metric = (value: number) => (current ? number(value) : 'Unavailable');
  return (
    <section className="tcvs-operations-environment" data-health={environment.health}>
      <header>
        <div>
          <h3>{environment.stage.toUpperCase()}</h3>
          <span>{environment.accountId}</span>
        </div>
        {environment.metricsStatus === 'NOT_REQUESTED' ? (
          <span className="tcvs-muted">On demand</span>
        ) : (
          <HealthBadge health={environment.health} />
        )}
        {environment.stage === 'dev' ? (
          <button
            type="button"
            className="tcvs-icon-button"
            disabled={refreshing}
            onClick={requestDiagnostics}
            title="Refresh Dev diagnostics"
            aria-label="Refresh Dev diagnostics"
          >
            <RefreshCw size={18} />
          </button>
        ) : null}
      </header>
      <p className="tcvs-muted">
        Metrics:{' '}
        {environment.metricsObservedAt ? age(environment.metricsObservedAt) : 'Not available'}
        {environment.metricsStatus === 'STALE' ? ' · Stale' : ''}
        {environment.stage === 'dev' ? ' · On demand' : ''}
      </p>
      {environment.unavailableReason !== undefined ? (
        <p className={environment.metricsStatus === 'NOT_REQUESTED' ? 'tcvs-muted' : 'tcvs-error'}>
          {environment.unavailableReason}
        </p>
      ) : null}
      <div className="tcvs-operations-stat-grid">
        <Metric label="API requests" value={metric(environment.apiRequests)} />
        <Metric
          label="Service errors"
          value={metric(
            environment.apiServerErrors + environment.lambdaErrors + environment.applicationErrors,
          )}
          warning={
            current &&
            environment.apiServerErrors + environment.lambdaErrors + environment.applicationErrors >
              0
          }
        />
        <Metric
          label="Throttles"
          value={metric(environment.lambdaThrottles + environment.dynamodbThrottles)}
          warning={current && environment.lambdaThrottles + environment.dynamodbThrottles > 0}
        />
        <Metric
          label="Queue depth"
          value={metric(environment.queueVisibleMessages)}
          warning={current && environment.deadLetterMessages > 0}
        />
        <Metric label="eBay inventory" value={age(environment.business.ebayInventoryUpdatedAt)} />
        <Metric label="eBay orders" value={age(environment.business.ebayOrdersUpdatedAt)} />
        <Metric label="Month to date" value={usd(environment.cost?.netUsd)} />
        <Metric label="Month forecast" value={usd(environment.cost?.forecastMonthEndUsd)} />
        <Metric
          label="Checklist models · 24h"
          value={
            current
              ? usdUnitCost(environment.modelEconomics.checklistModelEstimatedCost24hUsd)
              : 'Unavailable'
          }
        />
        <Metric
          label="Model cost / card · 24h"
          value={
            current
              ? usdUnitCost(environment.modelEconomics.checklistModelEstimatedCostPerCard24hUsd)
              : 'Unavailable'
          }
        />
        <Metric
          label="Bedrock · month to date"
          value={usd(environment.modelEconomics.bedrockCostMonthToDateUsd)}
        />
        <Metric
          label="AWS cost / captured card"
          value={usdUnitCost(environment.modelEconomics.awsCostPerCapturedCardMonthToDateUsd)}
        />
      </div>
      {environment.cost?.stale === true ? (
        <p className="tcvs-warning">Showing the last successful cost snapshot.</p>
      ) : null}
    </section>
  );
}

function HealthBadge({ health }: { health: OperationsHealth }): JSX.Element {
  return (
    <span className="tcvs-operations-health" data-health={health}>
      {health}
    </span>
  );
}

function hasCurrentMetrics(environment: OperationsEnvironmentSnapshot): boolean {
  return (
    environment.metricsStatus === 'FRESH' ||
    (environment.metricsStatus === undefined && environment.health !== 'UNAVAILABLE')
  );
}

function Metric({
  label,
  value,
  warning = false,
}: {
  label: string;
  value: string;
  warning?: boolean;
}): JSX.Element {
  return (
    <div className="tcvs-operations-metric" data-warning={warning ? 'true' : 'false'}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function AttentionList({
  environments,
  wallboard,
}: {
  environments: readonly OperationsEnvironmentSnapshot[];
  wallboard: boolean;
}): JSX.Element {
  const items = environments.filter(hasCurrentMetrics).flatMap((environment) => [
    ...environment.alarms.map((alarm) => ({
      key: `${environment.stage}:alarm:${alarm.name}`,
      stage: environment.stage,
      kind: alarm.state,
      name: alarm.name,
      detail: alarm.reason ?? 'Alarm requires review',
    })),
    ...environment.queues.map((queue) => ({
      key: `${environment.stage}:queue:${queue.name}`,
      stage: environment.stage,
      kind: queue.deadLetter ? 'DLQ' : 'QUEUE',
      name: queue.name,
      detail: `${number(queue.visibleMessages)} waiting · oldest ${number(queue.oldestMessageSeconds)}s`,
    })),
  ]);
  const visible = wallboard ? items.slice(0, 8) : items;
  if (visible.length === 0)
    return (
      <p className={environments.every(hasCurrentMetrics) ? 'tcvs-operations-clear' : 'tcvs-muted'}>
        {environments.every(hasCurrentMetrics)
          ? 'No active alarms or queued work.'
          : 'No alerts in available metrics; some environment diagnostics are unavailable.'}
      </p>
    );
  return (
    <div className="tcvs-operations-attention-list">
      {visible.map((item) => (
        <div key={item.key}>
          <span>{item.stage.toUpperCase()}</span>
          <strong>{item.kind}</strong>
          <div>
            <b>{item.name}</b>
            <small>{item.detail}</small>
          </div>
        </div>
      ))}
    </div>
  );
}

function ProcessTable({
  environments,
}: {
  environments: readonly OperationsEnvironmentSnapshot[];
}): JSX.Element {
  const rows = environments
    .filter(hasCurrentMetrics)
    .flatMap((environment) =>
      environment.activeProcesses.map((process) => ({
        ...process,
        stage: environment.stage,
      })),
    )
    .sort((left, right) => right.invocations - left.invocations)
    .slice(0, 5);
  if (rows.length === 0)
    return (
      <p className="tcvs-muted">
        {environments.every(hasCurrentMetrics)
          ? 'No Lambda activity was returned.'
          : 'Current activity diagnostics are unavailable.'}
      </p>
    );
  return (
    <div className="tcvs-table-wrap">
      <table className="tcvs-table tcvs-operations-table">
        <thead>
          <tr>
            <th>Environment</th>
            <th>Process</th>
            <th>Invocations</th>
            <th>Errors</th>
            <th>Throttles</th>
            <th>Avg duration</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={`${row.stage}:${row.name}`}>
              <td>{row.stage.toUpperCase()}</td>
              <td>{row.name.replace(/^tcvs-|-(dev|prod)$/g, '')}</td>
              <td>{number(row.invocations)}</td>
              <td>{number(row.errors)}</td>
              <td>{number(row.throttles)}</td>
              <td>{Math.round(row.averageDurationMs).toLocaleString()} ms</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
